// Dashboard anchors are authored against Axilon routes (`/plant`,
// `/dashboards/...`) but the click happens in the cross-origin sandbox
// iframe. Resolve the raw href against the parent page, then either
// move the top window. Even same-origin links use a full document
// navigation so the dashboard iframe and all page state are replaced.

const MAX_HREF_LENGTH = 4096

export interface ParentLocation {
  origin: string
  href: string
}

export type DashboardLinkResolution =
  | { kind: 'same-origin'; url: string }
  | { kind: 'external'; url: string }

export interface DashboardLinkActions {
  assign: (url: string) => void
  open: (url: string) => void
}

function hasControlCharOrBackslash(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i)
    if (code <= 0x1f || code === 0x7f || code === 0x5c) return true
  }
  return false
}

/**
 * Classify a raw anchor href. Returns null when the parent must ignore
 * the click (empty, hash-only, scriptable, or credentialed URLs).
 * Same-origin http(s) URLs remain distinguishable so they can be
 * revalidated before navigation. Every accepted URL is ultimately
 * handled by the parent window, never by the iframe.
 */
export function resolveDashboardLink(
  rawHref: string,
  parent: ParentLocation
): DashboardLinkResolution | null {
  if (typeof rawHref !== 'string') return null
  if (rawHref.length > MAX_HREF_LENGTH) return null
  // Reject before trim. `String.prototype.trim` strips newlines, and a
  // trailing newline would otherwise collapse into a normal path.
  if (hasControlCharOrBackslash(rawHref)) return null
  const href = rawHref.trim()
  if (href === '' || href.startsWith('#')) return null

  let url: URL
  try {
    url = new URL(href, parent.href)
  } catch {
    return null
  }

  if (url.username !== '' || url.password !== '') return null

  const protocol = url.protocol.toLowerCase()
  if (protocol === 'mailto:' || protocol === 'tel:') {
    return { kind: 'external', url: url.toString() }
  }
  if (protocol !== 'http:' && protocol !== 'https:') return null

  if (url.origin === parent.origin) {
    const to = `${url.pathname}${url.search}${url.hash}`
    // `//evil.com` is still this origin after one parse, then becomes a
    // different origin when React Router or `new URL` resolves it again.
    if (!to.startsWith('/') || to.startsWith('//')) return null
    if (new URL(to, parent.origin).origin !== parent.origin) return null
    return { kind: 'same-origin', url: url.toString() }
  }

  return { kind: 'external', url: url.toString() }
}

export function followDashboardLink(
  resolution: DashboardLinkResolution,
  newTab: boolean,
  actions: DashboardLinkActions,
  parentOrigin: string
): void {
  if (resolution.kind === 'same-origin') {
    let url: URL
    try {
      url = new URL(resolution.url)
    } catch {
      return
    }
    if (url.origin !== parentOrigin) return
    const to = `${url.pathname}${url.search}${url.hash}`
    if (!to.startsWith('/') || to.startsWith('//')) return
    if (newTab) {
      actions.open(url.toString())
      return
    }
    actions.assign(url.toString())
    return
  }
  if (newTab) {
    actions.open(resolution.url)
    return
  }
  actions.assign(resolution.url)
}
